import { useEffect, useState } from "react";
import { api, inr, formatErr } from "@/lib/api";
import { Bike, Receipt, Award, Sparkles, Calendar, Plus, Package as PkgIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const bookingStatusColor = {
  requested: "text-yellow-500 bg-yellow-500/10",
  confirmed: "text-blue-400 bg-blue-500/10",
  converted: "text-green-500 bg-green-500/10",
  cancelled: "text-red-500 bg-red-500/10",
};

export default function CustomerPortal() {
  const [vehicles, setVehicles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [b, setB] = useState({ vehicle_id: "", package_id: "", slot_date: "", slot_time: "10:00", notes: "" });

  const loadAll = () => {
    api.get("/vehicles").then(r => setVehicles(r.data));
    api.get("/invoices").then(r => setInvoices(r.data));
    api.get("/jobcards").then(r => setJobs(r.data));
    api.get("/loyalty/me").then(r => setLoyalty(r.data)).catch(() => {});
    api.get("/packages").then(r => setPackages(r.data)).catch(() => {});
    api.get("/bookings").then(r => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const submitBooking = async () => {
    if (!b.slot_date || !b.slot_time) { toast.error("Pick date and time"); return; }
    try {
      await api.post("/bookings", {
        vehicle_id: b.vehicle_id || null,
        package_id: b.package_id || null,
        slot_date: b.slot_date, slot_time: b.slot_time,
        notes: b.notes || null,
      });
      toast.success("Booking submitted — the shop will confirm shortly");
      setB({ vehicle_id: "", package_id: "", slot_date: "", slot_time: "10:00", notes: "" });
      setBookOpen(false);
      loadAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">My Portal</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Welcome back</h1>
        </div>
        <Dialog open={bookOpen} onOpenChange={setBookOpen}>
          <DialogTrigger asChild>
            <Button data-testid="book-service-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
              <Calendar className="w-4 h-4 mr-1"/> Book Service
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-display uppercase">Book a Service Slot</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle</Label>
                <Select value={b.vehicle_id} onValueChange={(v) => setB(x => ({ ...x, vehicle_id: v }))}>
                  <SelectTrigger data-testid="book-vehicle" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Choose a vehicle"/></SelectTrigger>
                  <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration} · {v.make} {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Service Package (optional)</Label>
                <Select value={b.package_id} onValueChange={(v) => setB(x => ({ ...x, package_id: v }))}>
                  <SelectTrigger data-testid="book-package" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="General service"/></SelectTrigger>
                  <SelectContent>
                    {packages.map(pkg => <SelectItem key={pkg.id} value={pkg.id}>{pkg.name} · {inr(pkg.labour_total)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                  <Input data-testid="book-date" type="date" min={today} value={b.slot_date} onChange={(e) => setB(x => ({ ...x, slot_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Time</Label>
                  <Input data-testid="book-time" type="time" value={b.slot_time} onChange={(e) => setB(x => ({ ...x, slot_time: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes (optional)</Label>
                <Textarea data-testid="book-notes" value={b.notes} onChange={(e) => setB(x => ({ ...x, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={3}/>
              </div>
              <Button data-testid="submit-booking-btn" onClick={submitBooking} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Request Slot</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loyalty && (
        <div className="border border-primary/40 bg-gradient-to-r from-primary/10 to-transparent p-5 rounded-sm mb-6 flex items-center gap-4">
          <div className="w-14 h-14 flex items-center justify-center bg-primary text-primary-foreground">
            <Award className="w-7 h-7" strokeWidth={2.5}/>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary flex items-center gap-1"><Sparkles className="w-3 h-3"/> Loyalty Balance</div>
            <div className="font-display font-black text-4xl leading-none mt-1">
              {loyalty.points} <span className="text-sm text-muted-foreground font-normal font-sans">pts</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">1 pt = ₹1 off · Earn 1 pt per ₹100 paid</div>
          </div>
        </div>
      )}

      {bookings.length > 0 && (
        <div className="border border-border bg-card p-5 rounded-sm mb-6">
          <h3 className="font-display font-bold text-lg uppercase mb-4 flex items-center gap-2"><Calendar className="w-4 h-4"/> My Bookings</h3>
          <div className="space-y-2">
            {bookings.slice(0, 3).map(bk => (
              <div key={bk.id} className="flex justify-between items-center border-b border-border pb-2">
                <div>
                  <div className="text-sm">{bk.slot_date} · {bk.slot_time}</div>
                  <div className="text-xs text-muted-foreground">{bk.package_name || "General service"}</div>
                </div>
                <span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm ${bookingStatusColor[bk.status]}`}>{bk.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {vehicles.map(v => (
          <div key={v.id} className="border border-border bg-card p-5 rounded-sm">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-primary/10 border border-primary/40 flex items-center justify-center">
                <Bike className="w-5 h-5 text-primary"/>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{v.vehicle_type}</span>
            </div>
            <div className="font-display font-black text-2xl uppercase tracking-tight">{v.make} {v.model}</div>
            <div className="font-mono text-primary text-sm mt-1">{v.registration}</div>
            <div className="text-xs text-muted-foreground mt-2">{v.year || "-"} · {v.odometer} km</div>
          </div>
        ))}
        {vehicles.length === 0 && <div className="text-muted-foreground text-sm">No vehicles linked to your account.</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card p-5 rounded-sm">
          <h3 className="font-display font-bold text-lg uppercase mb-4">Service History</h3>
          {jobs.length === 0 ? <div className="text-sm text-muted-foreground">No service history yet.</div> :
            <div className="space-y-3">
              {jobs.map(j => (
                <div key={j.id} className="border-b border-border pb-2">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xs text-primary">{j.job_number}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded-sm">{j.status.replace("_", " ")}</span>
                  </div>
                  <div className="text-sm mt-1">{j.complaints}</div>
                  <div className="text-xs text-muted-foreground mt-1">{new Date(j.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          }
        </div>

        <div className="border border-border bg-card p-5 rounded-sm">
          <h3 className="font-display font-bold text-lg uppercase mb-4 flex items-center gap-2"><Receipt className="w-4 h-4"/> Invoices</h3>
          {invoices.length === 0 ? <div className="text-sm text-muted-foreground">No invoices.</div> :
            <div className="space-y-3">
              {invoices.map(i => (
                <div key={i.id} className="border-b border-border pb-2 flex justify-between items-center">
                  <div>
                    <div className="font-mono text-xs text-primary">{i.invoice_number}</div>
                    <div className="text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{inr(i.total)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{i.status}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      </div>
    </div>
  );
}
